import { coerceDate, formatIsoDate, type DateLike } from '@/utils/date';

const MONTH_LABELS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
] as const;

const MONTH_WINDOW_FORMATTER = new Intl.DateTimeFormat('it-IT', {
  month: 'long',
  year: 'numeric',
});

type FrequencyKey =
  | 'monthly'
  | 'bimonthly'
  | 'quarterly'
  | 'four_monthly'
  | 'semiannual'
  | 'annual';

const FREQUENCY_LABELS: Record<FrequencyKey, string> = {
  monthly: 'Mensile',
  bimonthly: 'Bimestrale',
  quarterly: 'Trimestrale',
  four_monthly: 'Quadrimestrale',
  semiannual: 'Semestrale',
  annual: 'Annuale',
};

const FREQUENCY_TO_MONTHS: Record<FrequencyKey, number> = {
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  four_monthly: 4,
  semiannual: 6,
  annual: 12,
};

const FREQUENCY_MONTHS_TO_KEY: Record<number, FrequencyKey> = {
  1: 'monthly',
  2: 'bimonthly',
  3: 'quarterly',
  4: 'four_monthly',
  6: 'semiannual',
  12: 'annual',
};

const FREQUENCY_ALIASES: Record<string, FrequencyKey> = {
  mensal: 'monthly',
  mensile: 'monthly',
  monthly: 'monthly',
  '1': 'monthly',
  bimestral: 'bimonthly',
  bimestrale: 'bimonthly',
  bimonthly: 'bimonthly',
  '2': 'bimonthly',
  trimestral: 'quarterly',
  trimestrale: 'quarterly',
  quarterly: 'quarterly',
  '3': 'quarterly',
  quadrimestral: 'four_monthly',
  quadrimestrale: 'four_monthly',
  quadrimestralmente: 'four_monthly',
  four_monthly: 'four_monthly',
  'four-monthly': 'four_monthly',
  'four monthly': 'four_monthly',
  '4': 'four_monthly',
  semestral: 'semiannual',
  semestrale: 'semiannual',
  semiannual: 'semiannual',
  '6': 'semiannual',
  annual: 'annual',
  annuale: 'annual',
  '12': 'annual',
};

type SupportedFrequency = FrequencyKey;
const DEFAULT_FREQUENCY: SupportedFrequency = 'four_monthly';

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addMonths(date: Date, months: number): Date {
  const result = startOfDay(date);
  result.setMonth(result.getMonth() + months);
  return startOfDay(result);
}

function alignPeriodStart(date: Date, spanMonths: number): Date {
  const monthIndex = date.getMonth();
  const block = Math.floor(monthIndex / spanMonths);
  const startMonth = block * spanMonths;
  return new Date(date.getFullYear(), startMonth, 1);
}

function alignPeriodEnd(start: Date, spanMonths: number): Date {
  return new Date(start.getFullYear(), start.getMonth() + spanMonths, 0);
}

function capitalizeItalianMonth(label: string): string {
  if (!label) return label;
  return label.replace(/^[a-zà-ù]/iu, (match) => match.toUpperCase());
}

function mapAlias(label: string | undefined | null): SupportedFrequency | undefined {
  if (!label) return undefined;
  const normalized = label.trim().toLowerCase();
  return FREQUENCY_ALIASES[normalized];
}

function normalizeFrequency(label: string | undefined | null): SupportedFrequency | undefined {
  const direct = mapAlias(label);
  if (direct) return direct;
  if (!label) return undefined;
  const numeric = Number(label);
  if (!Number.isFinite(numeric)) return undefined;
  return FREQUENCY_MONTHS_TO_KEY[numeric];
}

export function frequencyToMonths(label: string | undefined | null): number | undefined {
  const normalized = normalizeFrequency(label);
  return normalized ? FREQUENCY_TO_MONTHS[normalized] : undefined;
}

export function formatFrequencyLabel(label: string | undefined | null): string {
  const normalized = normalizeFrequency(label) ?? DEFAULT_FREQUENCY;
  return FREQUENCY_LABELS[normalized];
}

export function frequencySummaryLabel(label: string | undefined | null): string {
  const normalized = normalizeFrequency(label) ?? DEFAULT_FREQUENCY;
  const months = FREQUENCY_TO_MONTHS[normalized];
  return `${FREQUENCY_LABELS[normalized]} (${months} mesi)`;
}

export function computeNextVisitDate(
  previousVisit: DateLike,
  frequency: string | undefined | null
): string | undefined {
  const months = frequencyToMonths(frequency);
  if (!months) return undefined;
  const base = coerceDate(previousVisit);
  if (!base) return undefined;
  const next = addMonths(base, months);
  return formatIsoDate(next);
}

function formatPeriodLabel(start: Date, end: Date): string {
  const startMonth = MONTH_LABELS[start.getMonth()];
  const endMonth = MONTH_LABELS[end.getMonth()];
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startMonth} ${startYear}`;
    }
    return `${startMonth}-${endMonth} ${startYear}`;
  }

  return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
}

export function formatControlPeriod(
  previousVisit: DateLike,
  frequency: string | undefined | null
): string | undefined {
  const months = frequencyToMonths(frequency);
  if (!months) return undefined;
  const base = coerceDate(previousVisit);
  if (!base) return undefined;
  const start = alignPeriodStart(base, months);
  const end = alignPeriodEnd(start, months);
  return formatPeriodLabel(start, end);
}

export function formatVisitControlPeriod(
  visitDate: DateLike,
  periodLengthMonths: number | undefined | null
): string | undefined {
  if (!periodLengthMonths) return undefined;

  const normalizedMonths = Math.max(1, Math.round(periodLengthMonths));
  const visit = coerceDate(visitDate);
  if (!visit) return undefined;

  const end = startOfDay(new Date(visit.getFullYear(), visit.getMonth(), 1));

  if (normalizedMonths === 1) {
    return `${MONTH_LABELS[end.getMonth()]} ${end.getFullYear()}`;
  }

  if (normalizedMonths % 12 === 0) {
    return String(end.getFullYear());
  }

  const start = new Date(end.getFullYear(), end.getMonth() - (normalizedMonths - 1), 1);

  const startLabel = `${MONTH_LABELS[start.getMonth()]} ${start.getFullYear()}`;
  const endLabel = `${MONTH_LABELS[end.getMonth()]} ${end.getFullYear()}`;
  return `${startLabel} - ${endLabel}`;
}

export function formatMonthWindow(value: DateLike): string | undefined {
  const date = coerceDate(value);
  if (!date) return undefined;
  const formatted = MONTH_WINDOW_FORMATTER.format(date);
  return capitalizeItalianMonth(formatted);
}

export interface MonthWindowDescriptor {
  label: string;
  isoValue: string;
  windowStartIso: string;
}

export function describeMonthWindow(value: DateLike): MonthWindowDescriptor | undefined {
  const date = coerceDate(value);
  if (!date) return undefined;
  const normalized = startOfDay(date);
  const label = formatMonthWindow(normalized);
  if (!label) return undefined;
  const isoValue = formatIsoDate(normalized);
  const windowStart = new Date(normalized.getFullYear(), normalized.getMonth(), 1);
  const windowStartIso = formatIsoDate(windowStart);
  return {
    label,
    isoValue,
    windowStartIso,
  };
}
